/**
 * Output refs `O-n` (wave K §4.2; one `O-n` per version chain, documents spec §2.5 / §13.2, decision 0065). New
 * outputs get their ref in decide (`PostArtifact`, carried in `ArtifactPosted` like `P-n`). A new version takes the
 * ref of the version it supersedes; only a new chain moves the counter. Older ones are numbered in post order by two
 * paths that agree:
 *
 * - a full replay: evolve gives an `ArtifactPosted` without a ref its chain's ref or the next number (`evolve.ts`);
 * - a snapshot from before the wave: {@link numberOutputs} numbers `state.artifacts` in array order, which is post
 *   order (`ArtifactPosted` appends), and the events after it continue the counter.
 */
import type { Ids, ProjectState } from "../types"

/** The output ref for number `n`. */
export const outputRef = (n: number): Ids["ShortRef"] => `O-${n}` as Ids["ShortRef"]

/**
 * Number a state's artifacts that have no ref, in array (= post) order, after the highest number already used; sets
 * `refCounters.O`. An artifact whose `supersedes` already has a ref (given or just numbered: it was posted earlier)
 * takes that ref. A no-op (the same object) when `refCounters.O` is already set or there is no artifact (a fold from
 * genesis leaves the counter absent until the first output, too).
 */
export const numberOutputs = (s: ProjectState): ProjectState => {
  if (s.refCounters.O !== undefined || s.artifacts.length === 0) return s
  let n = 0
  for (const a of s.artifacts) {
    const m = a.ref ? /^O-(\d+)$/.exec(a.ref) : null
    if (m) n = Math.max(n, Number(m[1]))
  }
  const refOf = new Map<string, Ids["ShortRef"]>()
  const artifacts = s.artifacts.map((a) => {
    const ref = a.ref ?? (a.supersedes ? refOf.get(a.supersedes) : undefined) ?? outputRef(++n)
    refOf.set(a.id, ref)
    return a.ref ? a : { ...a, ref }
  })
  return { ...s, artifacts, refCounters: { ...s.refCounters, O: n } }
}

/**
 * The ref a new artifact gets in a numbered state (`numberOutputs(s)`): the ref of the artifact it supersedes, else
 * the next number.
 */
export const refOfPost = (
  numbered: ProjectState,
  supersedes: Ids["ArtifactId"] | null | undefined,
): Ids["ShortRef"] => {
  const old = supersedes ? numbered.artifacts.find((a) => a.id === supersedes) : undefined
  return old?.ref ?? outputRef((numbered.refCounters.O ?? 0) + 1)
}
